export const THANK_YOU_NOTES_TITLE = "Thank You Notes";
export const TY_SEARCH_PLACEHOLDER = "Search and filter thank-you notes";
export const TY_FAVORITES_LABEL = "Favorites";
export const TY_EXPORT_LABEL = "Export CSV";
export const TY_CSV_FILENAME = "thank-you-notes.csv";
export const TY_EMPTY_TITLE = "No thank-you notes yet";
export const TY_EMPTY_SUBTITLE =
  "As recipients redeem their gifts, their thank-you notes will land here.";
export const TY_NO_MATCH = "No thank-you notes match these filters.";
export const TY_NO_FAVORITES = "No favorited notes yet.";
export const TY_BACK_LABELS = { campaign: "Campaign", redeemed: "Redeemed", gift: "Gift" } as const;
export const TY_FILTER_CAMPAIGN_LABEL = "Campaign";
export const TY_FILTER_SUBMITTED_LABEL = "Date submitted";
export const TY_VIEW_STORAGE_KEY = "thankYouNotesView";
export const TY_ERROR_MESSAGE =
  "We couldn't load thank-you notes. Check your connection and try again.";
export const TY_RETRY_LABEL = "Retry";
/**
 * Filter field ids, shared by the controls that declare the fields and the page that reads the
 * applied values. As bare literals the two ends drift silently: renaming one side compiles and
 * passes tests, the filter just stops matching.
 */
export const TY_FILTER_FIELD_IDS = { campaign: "campaign", submitted: "submitted" } as const;
export const TY_CHIP_SUBMITTED_PREFIX = "Submitted";

/**
 * Search debounce. Since GFG-234 the search term is SQL on the server, so every keystroke would
 * otherwise cost a round-trip; the input stays instantly controlled and only the settled value
 * reaches the query. 300ms is what `Blotter`, `Users` and `Campaigns` already use — one typing
 * feel across the app.
 */
export const TY_SEARCH_DEBOUNCE_MS = 300;

/** Export button label while the walk over every page is in flight (matches the Blotter's copy). */
export const TY_EXPORTING_LABEL = "Exporting…";

/**
 * Shown when the CSV export fails. Covers both failure modes deliberately — a request that errors
 * and the export walk's 10k-row safety cap — because the page cannot tell them apart without
 * reaching into the API layer's error types, and an axios message ("Request failed with status
 * code 500") is not copy to show a client. `console.error` keeps the real one.
 */
export const TY_EXPORT_ERROR =
  "We couldn't export these notes. Try again, or narrow the filters if the range is very large.";

/**
 * Upper bound on the free-text search term, MIRRORED from the server:
 * `apps/gfgf-api/src/constants/thankYouNotes.ts` exports `MAX_SEARCH_LENGTH` and is the source of
 * truth — the zod DTO rejects anything longer with a 400. The page clamps what the user types to
 * this so a long paste never becomes a failed request; if the server's cap moves, move this with
 * it. Deliberately NOT a `maxLength` prop on `BlotterFilterBar`: that bar is shared by 11 surfaces
 * and none of the others answer to this endpoint's limit.
 */
export const TY_MAX_SEARCH_LENGTH = 200;

/**
 * "Load more" pager. The feed asks for one page at a time (the API caps a page at 100 rows), so
 * without this control a client with more notes than that could only ever see the first page —
 * while Export CSV, which walks every page, handed them the whole set.
 */
export const TY_LOAD_MORE_LABEL = "Load more";

/** Its label while the next page is in flight, so a slow page can't be clicked twice. */
export const TY_LOADING_MORE_LABEL = "Loading…";

/**
 * Feed counter copy. Two forms, because the two numbers do not mean the same thing.
 *
 * `tyCountLabel` reports the server's FILTERED total — every note matching the current search,
 * campaign and date filters, not merely the pages fetched so far.
 */
export const tyCountLabel = (total: number): string => `${total} note${total === 1 ? "" : "s"}`;

/**
 * The Favorites-only form. That toggle is browser session state, so it is applied AFTER the rows
 * arrive and can only narrow the pages already loaded — the server's total is then not what the
 * user is looking at, and the favorited count is not a total. Both numbers are named rather than
 * one of them being passed off as the whole answer.
 */
export const tyFavoritesCountLabel = (favorites: number, loaded: number): string =>
  `${favorites} favorited of ${loaded} note${loaded === 1 ? "" : "s"} loaded`;
